import React, { useEffect, useMemo, useState } from 'react';
import MaterialTable from 'material-table';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import moment from 'moment/min/moment-with-locales';
import { ThemeProvider } from '@mui/material/styles';
import { Alert, Box, Chip, Grid, TextField, Typography } from '@mui/material';
import SosIcon from '@mui/icons-material/Sos';
import NotificationImportantOutlinedIcon from '@mui/icons-material/NotificationImportantOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { api } from 'common';
import { downloadCsv } from '../common/sharedFunctions';
import CircularLoading from '../components/CircularLoading';
import localization from '../components/Table/Localization';
import C2Dialog, { C2PrimaryButton, C2SecondaryButton } from '../components/C2Dialog';
import {
  C2,
  C2_FONT,
  c2TableTheme,
  c2TableOptions,
  c2TableStyle,
  c2Toolbar,
  c2Localization,
  C2TableCard,
  C2SectionHeading,
  C2EmptyState,
  C2DocsChip,
  C2Select,
  C2StatusChip,
  C2SummaryCard,
} from '../components/Table/C2UserTable';

const STATUS_UI = {
  pending: { tone: 'danger', label: 'Pendiente' },
  in_progress: { tone: 'warning', label: 'En atención' },
  resolved: { tone: 'success', label: 'Resuelta' },
  false_alarm: { tone: 'neutral', label: 'Falsa alarma' },
};

const USER_TYPES = { driver: 'Conductor', customer: 'Usuario' };

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    fontFamily: C2_FONT,
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: C2.border },
    '&:hover fieldset': { borderColor: C2.bright },
    '&.Mui-focused fieldset': { borderColor: C2.violet },
  },
  '& .MuiInputLabel-root': { fontFamily: C2_FONT, color: C2.muted },
  '& .MuiInputLabel-root.Mui-focused': { color: C2.violet },
};

const pillSx = (active, color = C2.violet) => ({
  fontFamily: C2_FONT,
  fontWeight: 700,
  borderRadius: '10px',
  border: `1px solid ${active ? color : C2.border}`,
  color: active ? '#FFFFFF' : C2.dark,
  backgroundColor: active ? color : '#FFFFFF',
  '&:hover': { backgroundColor: active ? color : C2.lavender },
});

const STATUS_COLORS = { pending: '#DC2626', in_progress: '#D97706', resolved: '#16A34A', false_alarm: '#64748B' };

const coordsOf = (s) => {
  const lat = Number(s?.location?.latitude);
  const lng = Number(s?.location?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0) ? { lat, lng } : null;
};
const mapsUrl = (c) => `https://www.google.com/maps/search/?api=1&query=${c.lat},${c.lng}`;
const phoneDigits = (v) => String(v || '').replace(/\D/g, '');
const waNumber = (v) => {
  const d = phoneDigits(v);
  return d.length === 10 ? `57${d}` : d;
};
const hasPhone = (v) => phoneDigits(v).length >= 7;
const userTypeLabel = (v) => USER_TYPES[v] || 'Sin tipo';

function InfoRow({ label, children }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.6, borderBottom: `1px solid ${C2.cellBorder}` }}>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted, flexShrink: 0 }}>{label}</Typography>
      <Box sx={{ fontFamily: C2_FONT, fontSize: '0.86rem', fontWeight: 700, color: C2.sidebar, textAlign: 'right', wordBreak: 'break-word' }}>{children}</Box>
    </Box>
  );
}

export default function Sos() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector((s) => s.settingsdata.settings);
  const sosdata = useSelector((s) => s.sosdata);
  const showContact = !!settings?.AllowCriticalEditsAdmin;

  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [draftStatus, setDraftStatus] = useState('pending');
  const [draftNote, setDraftNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const rows = useMemo(
    () =>
      [...(sosdata.sos || [])]
        .map((s) => ({ ...s, status: api.sosStatusOf(s) }))
        .sort((a, b) => (Number(b.complainDate) || 0) - (Number(a.complainDate) || 0)),
    [sosdata.sos]
  );

  const summary = useMemo(() => {
    const monthStart = moment().startOf('month').valueOf();
    return {
      pending: rows.filter((r) => r.status === 'pending').length,
      inProgress: rows.filter((r) => r.status === 'in_progress').length,
      resolvedMonth: rows.filter((r) => (r.status === 'resolved' || r.status === 'false_alarm') && (Number(r.statusUpdatedAt || r.processDate) || 0) >= monthStart).length,
      total: rows.length,
    };
  }, [rows]);

  const filtered = rows.filter((r) => (!statusFilter || r.status === statusFilter) && (!typeFilter || r.user_type === typeFilter));
  const pendingRows = rows.filter((r) => r.status === 'pending');
  const current = openId ? rows.find((r) => r.id === openId) : null;
  const currentCoords = coordsOf(current);

  const openAlert = (row) => {
    setOpenId(row.id);
    setDraftStatus(row.status === 'pending' ? 'in_progress' : row.status);
    setDraftNote(row.adminNote || '');
  };

  const saveStatus = async () => {
    if (!current) return;
    setSaving(true);
    try {
      await api.updateSosStatus(current.id, draftStatus, draftNote);
      setNotice({ severity: 'success', msg: `Alerta de ${current.user_name || 'usuario'} marcada como ${STATUS_UI[draftStatus].label.toLowerCase()}.` });
      setOpenId(null);
    } catch (e) {
      setNotice({ severity: 'error', msg: 'No se pudo actualizar la alerta. Inténtalo de nuevo.' });
    } finally {
      setSaving(false);
    }
  };

  const contactLinks = (row, size = 'small') => {
    if (!showContact || !hasPhone(row.contact)) return null;
    return (
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: size === 'small' ? 'flex-start' : 'flex-end' }}>
        <Chip
          component="a"
          href={`tel:${phoneDigits(row.contact)}`}
          clickable
          size="small"
          icon={<PhoneOutlinedIcon />}
          label="Llamar"
          onClick={(e) => e.stopPropagation()}
          sx={{ fontFamily: C2_FONT, fontWeight: 700, backgroundColor: C2.lavender, color: C2.violet, '& .MuiChip-icon': { color: C2.violet } }}
        />
        <Chip
          component="a"
          href={`https://wa.me/${waNumber(row.contact)}`}
          target="_blank"
          rel="noopener noreferrer"
          clickable
          size="small"
          icon={<WhatsAppIcon />}
          label="WhatsApp"
          onClick={(e) => e.stopPropagation()}
          sx={{ fontFamily: C2_FONT, fontWeight: 700, backgroundColor: '#DCFCE7', color: '#15803D', '& .MuiChip-icon': { color: '#15803D' } }}
        />
      </Box>
    );
  };

  const columns = [
    {
      title: 'Fecha',
      field: 'complainDate',
      render: (r) => (
        <Box sx={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.85rem', color: C2.sidebar }}>{r.complainDate ? moment(r.complainDate).format('lll') : '—'}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: r.status === 'pending' ? '#B91C1C' : C2.muted, fontWeight: r.status === 'pending' ? 700 : 400 }}>
            {r.complainDate ? moment(r.complainDate).fromNow() : ''}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Quién pidió ayuda',
      field: 'user_name',
      render: (r) => (
        <Box sx={{ textAlign: 'left', minWidth: 150 }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>{r.user_name || '—'}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: C2.muted }}>
            {userTypeLabel(r.user_type)}
            {showContact && r.contact ? ` · ${r.contact}` : ''}
          </Typography>
        </Box>
      ),
    },
    { title: 'Contacto', field: 'contact', sorting: false, render: (r) => contactLinks(r) || <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted }}>{showContact ? 'Sin teléfono' : t('hidden_demo')}</Typography> },
    {
      title: 'Ubicación',
      field: 'location',
      sorting: false,
      render: (r) => {
        const c = coordsOf(r);
        if (!c) return <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted }}>Sin ubicación</Typography>;
        return (
          <Box sx={{ textAlign: 'left' }}>
            <Chip
              component="a"
              href={mapsUrl(c)}
              target="_blank"
              rel="noopener noreferrer"
              clickable
              size="small"
              icon={<PlaceOutlinedIcon />}
              label="Ver mapa"
              onClick={(e) => e.stopPropagation()}
              sx={{ fontFamily: C2_FONT, fontWeight: 700, backgroundColor: '#FEE2E2', color: '#B91C1C', '& .MuiChip-icon': { color: '#B91C1C' } }}
            />
            {Number(r.location?.accuracy) > 0 ? (
              <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.72rem', color: C2.muted, mt: 0.25 }}>± {Math.round(r.location.accuracy)} m</Typography>
            ) : null}
          </Box>
        );
      },
    },
    {
      title: 'Viaje',
      field: 'bookingId',
      render: (r) =>
        r.bookingId ? (
          <RouterLink to={`/bookings/bookingdetails/${r.bookingId}`} onClick={(e) => e.stopPropagation()} style={{ color: C2.violet, fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.82rem' }}>
            Ver viaje
          </RouterLink>
        ) : (
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted }}>Fuera de viaje</Typography>
        ),
    },
    {
      title: 'Estado',
      field: 'status',
      render: (r) => (
        <Box sx={{ textAlign: 'left' }}>
          <C2StatusChip tone={STATUS_UI[r.status].tone} label={STATUS_UI[r.status].label} />
          {r.attendedByEmail && r.status !== 'pending' ? (
            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.7rem', color: C2.muted, mt: 0.4 }}>{showContact ? r.attendedByEmail : 'Admin'}</Typography>
          ) : null}
        </Box>
      ),
    },
  ];

  const exportCsv = () => {
    const header = ['Fecha', 'Nombre', 'Tipo', 'Contacto', 'Viaje', 'Latitud', 'Longitud', 'Estado', 'Atendió', 'Nota'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = filtered.map((r) => {
      const c = coordsOf(r);
      return [
        r.complainDate ? moment(r.complainDate).format('YYYY-MM-DD HH:mm') : '',
        r.user_name,
        userTypeLabel(r.user_type),
        r.contact,
        r.bookingId || '',
        c ? c.lat : '',
        c ? c.lng : '',
        STATUS_UI[r.status].label,
        r.attendedByEmail || '',
        r.adminNote || '',
      ]
        .map(esc)
        .join(',');
    });
    downloadCsv('\uFEFF' + [header.map(esc).join(','), ...lines].join('\n'), `alertas-sos-${moment().format('YYYY-MM-DD')}.csv`);
  };

  const toolbar = c2Toolbar(
    <C2Select
      key="status"
      id="sos-status"
      label="Estado"
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      minWidth={160}
      options={[{ value: '', label: 'Todos' }, ...Object.keys(STATUS_UI).map((k) => ({ value: k, label: STATUS_UI[k].label }))]}
    />,
    <C2Select
      key="type"
      id="sos-type"
      label="Tipo"
      value={typeFilter}
      onChange={(e) => setTypeFilter(e.target.value)}
      minWidth={150}
      options={[{ value: '', label: 'Todos' }, { value: 'driver', label: 'Conductores' }, { value: 'customer', label: 'Usuarios' }]}
    />,
    showContact && filtered.length ? (
      <C2SecondaryButton key="csv" onClick={exportCsv}>
        Exportar CSV
      </C2SecondaryButton>
    ) : null
  );

  if (sosdata.loading) return <CircularLoading />;

  return (
    <Box sx={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <ThemeProvider theme={c2TableTheme}>
        {notice ? (
          <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}>
            {notice.msg}
          </Alert>
        ) : null}

        {pendingRows.length ? (
          <Box
            sx={{
              mb: 2,
              p: { xs: 2, sm: 2.5 },
              borderRadius: '16px',
              color: '#FFFFFF',
              background: 'linear-gradient(120deg, #991B1B 0%, #DC2626 60%, #7628FF 140%)',
              boxShadow: '0 12px 28px rgba(220,38,38,0.28)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '14px',
                backgroundColor: 'rgba(255,255,255,0.16)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'c2SosPulse 1.6s ease-in-out infinite',
                '@keyframes c2SosPulse': { '0%, 100%': { boxShadow: '0 0 0 0 rgba(255,255,255,0.45)' }, '50%': { boxShadow: '0 0 0 10px rgba(255,255,255,0)' } },
              }}
            >
              <SosIcon sx={{ fontSize: 30 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '1.05rem' }}>
                {pendingRows.length === 1 ? '1 alerta pendiente' : `${pendingRows.length} alertas pendientes`}
              </Typography>
              <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.86rem', opacity: 0.9 }}>
                La más reciente: {pendingRows[0].user_name || 'usuario'} ({userTypeLabel(pendingRows[0].user_type).toLowerCase()}),{' '}
                {pendingRows[0].complainDate ? moment(pendingRows[0].complainDate).fromNow() : 'sin fecha'}.
              </Typography>
            </Box>
            <C2PrimaryButton
              onClick={() => openAlert(pendingRows[0])}
              sx={{ background: '#FFFFFF', color: '#B91C1C', boxShadow: 'none', '&:hover': { background: '#FEE2E2', boxShadow: 'none' } }}
            >
              Atender ahora
            </C2PrimaryButton>
          </Box>
        ) : null}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={NotificationImportantOutlinedIcon} label="Pendientes" value={summary.pending} caption="Sin atender" accent={summary.pending ? '#DC2626' : C2.violet} />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={SupportAgentOutlinedIcon} label="En atención" value={summary.inProgress} caption="Un admin las está gestionando" accent="#D97706" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={TaskAltOutlinedIcon} label="Cerradas este mes" value={summary.resolvedMonth} caption="Resueltas o falsas alarmas" accent="#16A34A" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={ListAltOutlinedIcon} label="Total de alertas" value={summary.total} caption="Desde el inicio" />
          </Grid>
        </Grid>

        <C2TableCard>
          <C2SectionHeading title="Alertas SOS" subtitle="Se generan cuando un conductor o usuario pulsa SOS en la app. Contacta a la persona y registra cómo se resolvió." />
          <MaterialTable
            title={t('sos_title')}
            columns={columns}
            data={filtered}
            style={c2TableStyle(isRTL)}
            components={toolbar}
            onRowClick={(evt, row) => {
              setSelectedRow(row.tableData.id);
              openAlert(row);
            }}
            options={{
              ...c2TableOptions(selectedRow),
              actionsColumnIndex: -1,
              pageSize: 10,
              pageSizeOptions: [10, 20, 50],
              rowStyle: (row) => ({
                backgroundColor: row.status === 'pending' ? '#FEF2F2' : selectedRow === row.tableData.id ? C2.lavender : '#FFFFFF',
                fontFamily: C2_FONT,
              }),
            }}
            localization={c2Localization(
              localization(t),
              <C2EmptyState
                icon={SosIcon}
                title={statusFilter || typeFilter ? 'No hay alertas con estos filtros' : 'No hay alertas SOS'}
                message="Cuando alguien pulse SOS en la app, la alerta aparecerá aquí con su ubicación y datos de contacto."
              />
            )}
            actions={[
              (row) => ({
                icon: () => <C2DocsChip icon={SupportAgentOutlinedIcon} label={row.status === 'pending' ? 'Atender' : 'Ver'} />,
                tooltip: row.status === 'pending' ? 'Atender alerta' : 'Ver detalle',
                onClick: (e, r) => openAlert(r),
              }),
            ]}
          />
        </C2TableCard>

        <C2Dialog
          open={!!current}
          onClose={() => (saving ? null : setOpenId(null))}
          title={current ? `SOS · ${current.user_name || 'Usuario'}` : 'Alerta SOS'}
          icon={SosIcon}
          maxWidth={720}
          actions={
            <>
              <C2SecondaryButton onClick={() => setOpenId(null)} disabled={saving}>
                Cerrar
              </C2SecondaryButton>
              <C2PrimaryButton onClick={saveStatus} disabled={saving || !current || (draftStatus === current.status && (draftNote || '') === (current.adminNote || ''))}>
                {saving ? 'Guardando…' : 'Guardar'}
              </C2PrimaryButton>
            </>
          }
        >
          {current ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {currentCoords ? (
                  <Box sx={{ borderRadius: '14px', overflow: 'hidden', border: `1px solid ${C2.border}`, height: 240, backgroundColor: C2.lavender }}>
                    <iframe
                      title="Ubicación de la alerta"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://maps.google.com/maps?q=${currentCoords.lat},${currentCoords.lng}&z=16&output=embed`}
                    />
                  </Box>
                ) : (
                  <Box sx={{ height: 240, borderRadius: '14px', border: `1px dashed ${C2.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: C2.muted, gap: 1 }}>
                    <PlaceOutlinedIcon />
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.85rem' }}>La alerta no trae ubicación</Typography>
                  </Box>
                )}
                {currentCoords ? (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: C2.muted }}>
                      {currentCoords.lat.toFixed(5)}, {currentCoords.lng.toFixed(5)}
                      {Number(current.location?.accuracy) > 0 ? ` · ± ${Math.round(current.location.accuracy)} m` : ''}
                    </Typography>
                    <Chip
                      component="a"
                      href={mapsUrl(currentCoords)}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable
                      size="small"
                      icon={<OpenInNewRoundedIcon />}
                      label="Google Maps"
                      sx={{ fontFamily: C2_FONT, fontWeight: 700, backgroundColor: C2.lavender, color: C2.violet, '& .MuiChip-icon': { color: C2.violet } }}
                    />
                  </Box>
                ) : null}
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoRow label="Fecha">{current.complainDate ? `${moment(current.complainDate).format('lll')} (${moment(current.complainDate).fromNow()})` : '—'}</InfoRow>
                <InfoRow label="Tipo">{userTypeLabel(current.user_type)}</InfoRow>
                <InfoRow label="Teléfono">{showContact ? current.contact || 'Sin teléfono' : t('hidden_demo')}</InfoRow>
                <InfoRow label="Viaje">
                  {current.bookingId ? (
                    <RouterLink to={`/bookings/bookingdetails/${current.bookingId}`} style={{ color: C2.violet }}>
                      Ver viaje
                    </RouterLink>
                  ) : (
                    'Fuera de viaje'
                  )}
                </InfoRow>
                <InfoRow label="Estado">
                  <C2StatusChip tone={STATUS_UI[current.status].tone} label={STATUS_UI[current.status].label} />
                </InfoRow>
                {current.statusUpdatedAt ? (
                  <InfoRow label="Última gestión">
                    {moment(current.statusUpdatedAt).format('lll')}
                    {showContact && current.attendedByEmail ? ` · ${current.attendedByEmail}` : ''}
                  </InfoRow>
                ) : null}
                <Box sx={{ mt: 1.5 }}>{contactLinks(current, 'large')}</Box>
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.86rem', color: C2.sidebar, mb: 1 }}>Actualizar estado</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                  {Object.keys(STATUS_UI).map((k) => (
                    <Chip key={k} label={STATUS_UI[k].label} onClick={() => setDraftStatus(k)} sx={pillSx(draftStatus === k, STATUS_COLORS[k])} />
                  ))}
                </Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Nota de seguimiento"
                  placeholder="Ej.: se llamó al conductor, está bien; se contactó a la policía…"
                  value={draftNote}
                  onChange={(e) => setDraftNote(e.target.value.slice(0, 500))}
                  helperText={`${draftNote.length}/500`}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
          ) : null}
        </C2Dialog>
      </ThemeProvider>
    </Box>
  );
}
